import React, { useRef, useEffect, useCallback } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';

/**
 * CommandInterface — the signature terminal-style command input.
 *
 * Props:
 *  inputRef       — ref to forward focus to this input (for Cmd+K)
 *  input          — controlled input value
 *  history        — array of { type: 'input'|'output', text, isError }
 *  suggestions    — array of autocomplete strings
 *  suggestionIndex— currently highlighted suggestion index
 *  handleInputChange — (value) => void
 *  handleKeyDown     — keyboard event handler
 *  execute           — (rawInput) => void
 *  acceptSuggestion  — (str) => void
 */
const CommandInterface = ({
  inputRef,
  input,
  history,
  suggestions,
  suggestionIndex,
  handleInputChange,
  handleKeyDown,
  execute,
  acceptSuggestion,
}) => {
  const { ref: headRef, isVisible: headVisible } = useScrollReveal({ threshold: 0.2 });
  const outputRef = useRef(null);

  // Auto-scroll output to bottom
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  // Welcome message in history display
  const hasHistory = history.length > 0;

  return (
    <section
      id="command"
      className="py-24 md:py-36"
      style={{ backgroundColor: 'var(--color-bg2)', borderTop: '1px solid var(--color-border)' }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div ref={headRef} className="mb-10 md:mb-14">
          <div
            className="section-label mb-6"
            style={{ opacity: headVisible ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            COMMAND INTERFACE
          </div>
          <h2
            className="font-bold leading-none tracking-tight mb-3"
            style={{
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              opacity: headVisible ? 1 : 0,
              transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            CONTROL THE PORTFOLIO.
          </h2>
          <p
            className="font-mono text-sm"
            style={{
              color: 'var(--color-text2)',
              opacity: headVisible ? 1 : 0,
              transition: 'opacity 0.5s ease 0.3s',
            }}
          >
            Type a command below or press{' '}
            <kbd
              className="px-1.5 py-0.5 text-xs"
              style={{
                border: '1px solid var(--color-border)',
                color: 'var(--color-text)',
                background: 'var(--color-surface)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              ⌘K
            </kbd>{' '}
            anywhere. Not AI — deterministic commands only.
          </p>
        </div>

        {/* Terminal window */}
        <div
          className="cmd-surface max-w-3xl"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s',
          }}
        >
          {/* Terminal top bar */}
          <div
            className="flex items-center gap-2 px-4 py-3"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <div className="flex gap-1.5" aria-hidden="true">
              <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
            </div>
            <span
              className="font-mono text-xs ml-2"
              style={{ color: 'var(--color-text2)' }}
            >
              jay-portfolio — command
            </span>
          </div>

          {/* Output area */}
          <div
            ref={outputRef}
            className="px-4 py-4 overflow-y-auto"
            style={{ minHeight: '160px', maxHeight: '320px' }}
          >
            {!hasHistory && (
              <div className="cmd-output">
                <span style={{ color: 'var(--color-accent)' }}>Welcome.</span>
                {'\n'}Type{' '}
                <span style={{ color: 'var(--color-text)' }}>help</span> to see all available commands.{'\n'}
                Use{' '}
                <span style={{ color: 'var(--color-text)' }}>↑ ↓</span> to navigate history,{' '}
                <span style={{ color: 'var(--color-text)' }}>Tab</span> to autocomplete.
              </div>
            )}

            {history.map((entry, i) => (
              <div key={i} className="mb-2">
                {entry.type === 'input' ? (
                  <div
                    className="font-mono text-sm flex items-center gap-2"
                    style={{ color: 'var(--color-text)' }}
                  >
                    <span style={{ color: 'var(--color-accent)' }}>{'>'}</span>
                    <span>{entry.text}</span>
                  </div>
                ) : (
                  <div
                    className="cmd-output mt-1 ml-5"
                    style={{ color: entry.isError ? '#ff6b6b' : 'var(--color-text2)' }}
                  >
                    {entry.text}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Autocomplete suggestions */}
          {suggestions.length > 0 && (
            <div style={{ borderTop: '1px solid var(--color-border)' }}>
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  className={`autocomplete-item w-full text-left ${i === suggestionIndex ? 'active' : ''}`}
                  onClick={() => acceptSuggestion(s)}
                  tabIndex={-1}
                >
                  {/* Highlight matched portion */}
                  <span className="match-highlight">{input}</span>
                  <span>{s.slice(input.length)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="cmd-input-row">
            <span className="cmd-prefix" aria-hidden="true">{'>'}</span>
            <input
              ref={inputRef}
              type="text"
              className="cmd-input"
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="type a command..."
              aria-label="Command input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <span className="cmd-cursor" aria-hidden="true" />
          </div>
        </div>

        {/* Quick command hints */}
        <div
          className="mt-6 flex flex-wrap gap-2"
          style={{
            opacity: headVisible ? 1 : 0,
            transition: 'opacity 0.5s ease 0.6s',
          }}
        >
          {[
            'show projects',
            'show ai projects',
            'show mobile projects',
            'open mintimoney',
            'show experience',
            'help',
          ].map((hint) => (
            <button
              key={hint}
              onClick={() => execute(hint)}
              className="font-mono text-xs px-3 py-1.5 transition-all duration-200"
              style={{
                border: '1px solid var(--color-border)',
                color: 'var(--color-text2)',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-text2)';
                e.currentTarget.style.color = 'var(--color-text)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.color = 'var(--color-text2)';
              }}
            >
              {hint}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CommandInterface;
